import { api } from './api';
import { fmtAuditDateTime } from './audit-time';

/* Types dérivés du contrat tRPC plutôt que recopiés : une colonne ajoutée côté
   serveur apparaît ici sans édition, et une colonne retirée casse à la
   compilation au lieu de mentir à l'affichage. */
type ListOutput = Awaited<ReturnType<typeof api.admin.safeguarding.list.query>>;
type DetailOutput = NonNullable<Awaited<ReturnType<typeof api.admin.safeguarding.get.mutate>>>;

export type SafeguardingAlertRow = ListOutput['items'][number];
export type SafeguardingAlertDetail = DetailOutput;
export type SafeguardingAlertFilter = NonNullable<
  Exclude<Parameters<typeof api.admin.safeguarding.list.query>[0], void>
>;
export type SafeguardingStatus = NonNullable<SafeguardingAlertFilter['status']>;

export async function fetchAlerts(filter?: SafeguardingAlertFilter): Promise<ListOutput> {
  return api.admin.safeguarding.list.query(filter);
}

/* Mutation et non query : l'appel ÉCRIT une trace de lecture. Une query serait
   rejouée par le cache du client et fabriquerait des consultations qui n'ont
   jamais eu lieu — la trace doit rester le reflet exact des accès réels. */
export async function fetchAlert(
  id: string,
  action: 'open_alert' | 'open_conversation' = 'open_alert',
): Promise<SafeguardingAlertDetail | null> {
  return api.admin.safeguarding.get.mutate({ id, action });
}

export type SafeguardingHealth = Awaited<ReturnType<typeof api.admin.safeguarding.health.query>>;

export async function fetchSafeguardingHealth(): Promise<SafeguardingHealth> {
  return api.admin.safeguarding.health.query();
}

/* Au-delà, une liste vide cesse d'être lisible comme « rien à signaler ». */
const STALE_AFTER_MS = 60 * 60 * 1000;
/* Le heartbeat n'est écrit qu'une fois par 5 min : un message plus récent que
   lui dans cette marge n'est pas une anomalie. */
const HEARTBEAT_THROTTLE_MS = 5 * 60 * 1000;

export function healthVerdict(h: SafeguardingHealth | null): {
  ok: boolean;
  label: string;
} {
  if (!h || !h.lastRunAt) {
    return { ok: false, label: "Le contrôle n'a JAMAIS tourné — une liste vide ne veut rien dire" };
  }
  const age = Date.now() - new Date(h.lastRunAt).getTime();
  if (Number.isNaN(age) || age > STALE_AFTER_MS) {
    const lastMessage = h.lastMessageAt ? new Date(h.lastMessageAt).getTime() : null;
    const unscreenedTraffic =
      lastMessage !== null && lastMessage - new Date(h.lastRunAt).getTime() > HEARTBEAT_THROTTLE_MS;
    if (unscreenedTraffic) {
      return {
        ok: false,
        label: `⚠️ Des messages ont été envoyés (dernier : ${fmtDateTime(h.lastMessageAt as string)}) mais le contrôle n'a pas tourné depuis ${fmtDateTime(h.lastRunAt)} — vérifier le déploiement`,
      };
    }
    return {
      ok: true,
      label: `Aucun message depuis ${fmtDateTime(h.lastRunAt)} : rien à analyser`,
    };
  }
  return { ok: true, label: `Contrôle actif — dernier passage ${fmtDateTime(h.lastRunAt)}, fenêtre de ${h.windowSize}` };
}

export async function setAlertStatus(id: string, status: SafeguardingStatus): Promise<void> {
  await api.admin.safeguarding.setStatus.mutate({ id, status });
}

export const SEVERITY_LABELS: Record<string, { label: string; hint: string }> = {
  person_at_risk: {
    label: 'Personne en danger',
    hint: "Danger pour soi ou pour autrui. Notification immédiate — à traiter sans attendre.",
  },
  legal_risk: {
    label: 'Risque légal',
    hint: "Demande relevant possiblement d'une infraction grave. Se consulte, ne réveille personne.",
  },
};

export const CATEGORY_LABELS: Record<string, string> = {
  self_harm: 'Danger pour soi',
  harm_to_others: 'Danger pour autrui',
  abuse_disclosure: 'Violences subies',
  serious_offence: 'Infraction grave',
  other: 'Autre',
};

export const STATUS_LABELS: Record<string, string> = {
  open: 'À traiter',
  in_review: 'En cours',
  closed_no_action: 'Clos — sans suite',
  closed_actioned: 'Clos — suite donnée',
};

export const SOURCE_LABELS: Record<string, string> = {
  model_tool: "Outil appelé par le modèle",
  server_screen: 'Contrôle serveur',
};

export function fmtDateTime(iso: string): string {
  return fmtAuditDateTime(iso);
}
